/**
 * Scene 6: The Growth Champions
 * "Rising Stars"
 */

import { useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { AnimatedPercentage } from '../AnimatedCounter';
import { GrowthArrowIcon, DeclineArrowIcon, StarIcon } from '../../icons/StoryIcons';
import type { StoryInsights } from '../../../hooks/useStoryData';

gsap.registerPlugin(ScrollTrigger);

interface Scene6Props {
  insights: StoryInsights;
  isActive: boolean;
}

export function Scene6Growth({ insights, isActive }: Scene6Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const topGrowthRef = useRef<HTMLDivElement>(null);
  const bottomGrowthRef = useRef<HTMLDivElement>(null);
  const sparklineContainerRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    if (!containerRef.current) return;
    
    const ctx = gsap.context(() => {
      // Only set initial hidden state if not active
      if (!isActive) {
        gsap.set(titleRef.current, { opacity: 0, y: 50 });
        gsap.set(topGrowthRef.current, { opacity: 0, x: -50 });
        gsap.set(bottomGrowthRef.current, { opacity: 0, x: 50 });
        
        if (sparklineContainerRef.current) {
          const cards = sparklineContainerRef.current.querySelectorAll('.growth-card');
          gsap.set(cards, { opacity: 0, y: 30, rotateY: -15 });
        }
      }
      
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          start: 'top center',
          end: 'center center',
          toggleActions: 'play none none reverse',
        },
      });
      
      tl.to(titleRef.current, {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: 'power3.out',
      })
      .to(topGrowthRef.current, {
        opacity: 1,
        x: 0,
        duration: 0.8,
        ease: 'power3.out',
      }, '-=0.4')
      .to(bottomGrowthRef.current, {
        opacity: 1,
        x: 0,
        duration: 0.8,
        ease: 'power3.out',
      }, '-=0.6');
      
      if (sparklineContainerRef.current) {
        const cards = sparklineContainerRef.current.querySelectorAll('.growth-card');
        tl.to(cards, {
          opacity: 1,
          y: 0,
          rotateY: 0,
          duration: 0.6,
          stagger: 0.1,
          ease: 'power3.out',
        }, '-=0.4');
      }
    }, containerRef);
    
    return () => ctx.revert();
  }, []);
  
  // Force visibility when scene becomes active (for auto-scroll)
  useEffect(() => {
    if (isActive) {
      gsap.to(titleRef.current, { opacity: 1, y: 0, duration: 0.5 });
      gsap.to(topGrowthRef.current, { opacity: 1, x: 0, duration: 0.5 });
      gsap.to(bottomGrowthRef.current, { opacity: 1, x: 0, duration: 0.5 });
      
      if (sparklineContainerRef.current) {
        const cards = sparklineContainerRef.current.querySelectorAll('.growth-card');
        gsap.to(cards, { opacity: 1, y: 0, rotateY: 0, duration: 0.5, stagger: 0.1 });
      }
    }
  }, [isActive]);
  
  // Generate a simple sparkline path
  const generateSparkline = (startTotal: number, endTotal: number) => {
    const points = [];
    const steps = 10;
    const width = 100;
    const height = 40;
    
    for (let i = 0; i <= steps; i++) {
      const progress = i / steps;
      // Add some variation with easing
      const value = startTotal + (endTotal - startTotal) * Math.pow(progress, 0.7) + 
        (Math.random() - 0.5) * (endTotal - startTotal) * 0.2;
      
      const x = (i / steps) * width;
      const y = height - ((value - startTotal) / (endTotal - startTotal || 1)) * height * 0.8 - height * 0.1;
      
      points.push(`${i === 0 ? 'M' : 'L'} ${x.toFixed(1)},${Math.max(5, Math.min(height - 5, y)).toFixed(1)}`);
    }
    
    return points.join(' ');
  };
  
  return (
    <section
      ref={containerRef}
      className="min-h-screen flex flex-col items-center justify-center relative overflow-hidden px-4 py-20"
    >
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-1/2 bg-gradient-to-b from-success-500/5 to-transparent" />
        <div className="absolute bottom-0 left-0 w-full h-1/2 bg-gradient-to-t from-danger-500/5 to-transparent" />
      </div>
      
      {/* Floating stars */}
      <div className="absolute inset-0 pointer-events-none">
        {Array.from({ length: 6 }, (_, i) => (
          <StarIcon
            key={i}
            className="absolute text-accent-500/20 animate-pulse"
            style={{
              left: `${15 + i * 15}%`,
              top: `${10 + (i % 3) * 20}%`,
              animationDelay: `${i * 0.5}s`,
            }}
            size={12 + i * 4}
          />
        ))}
      </div>
      
      {/* Content */}
      <div className="relative z-10 max-w-5xl w-full">
        {/* Title */}
        <div ref={titleRef} className="text-center mb-12">
          <div className="flex items-center justify-center gap-4 mb-4">
            <GrowthArrowIcon size={40} className="text-success-400" />
            <h2 className="text-3xl md:text-5xl font-display font-bold text-white">
              The Growth Champions
            </h2>
          </div>
          <p className="text-xl md:text-2xl text-surface-300">
            Which states have grown the most since{' '}
            <span className="text-accent-400">{insights.yearsSpan.start}</span>?
          </p>
        </div>
        
        {/* Top and Bottom Growth comparison */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {/* Top Growth */}
          <div ref={topGrowthRef}>
            <div className="flex items-center gap-3 mb-4 group cursor-default">
              <GrowthArrowIcon size={24} className="text-success-400 transition-transform duration-300 group-hover:scale-110 group-hover:-translate-y-1" />
              <h3 className="text-xl font-semibold text-success-400 transition-colors duration-300 group-hover:text-success-300">Highest Growth</h3>
            </div>
            
            <div className="space-y-3">
              {insights.topGrowthStates.map((state, index) => (
                <div
                  key={state.state}
                  className="group/card p-4 bg-success-500/10 rounded-xl border border-success-500/30 flex items-center gap-4 cursor-pointer transition-all duration-300 hover:bg-success-500/20 hover:border-success-500/50 hover:shadow-lg hover:shadow-success-500/10 hover:-translate-y-1 hover:scale-[1.02]"
                >
                  <div className="w-8 h-8 rounded-full bg-success-500 text-surface-900 flex items-center justify-center font-bold transition-all duration-300 group-hover/card:scale-110 group-hover/card:shadow-md group-hover/card:shadow-success-500/50">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-white transition-colors duration-300 group-hover/card:text-success-200">{state.state}</div>
                  </div>
                  <div className="text-2xl font-bold text-success-400 transition-all duration-300 group-hover/card:text-success-300 group-hover/card:scale-110">
                    +<AnimatedPercentage 
                      target={Math.min(state.percentGrowth, 9999)} 
                      decimals={0}
                      animate={isActive} 
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Bottom Growth */}
          <div ref={bottomGrowthRef}>
            <div className="flex items-center gap-3 mb-4 group cursor-default">
              <DeclineArrowIcon size={24} className="text-danger-400 transition-transform duration-300 group-hover:scale-110 group-hover:translate-y-1" />
              <h3 className="text-xl font-semibold text-danger-400 transition-colors duration-300 group-hover:text-danger-300">Lowest Growth</h3>
            </div>
            
            <div className="space-y-3">
              {insights.bottomGrowthStates.map((state, index) => (
                <div
                  key={state.state}
                  className="group/card p-4 bg-danger-500/10 rounded-xl border border-danger-500/30 flex items-center gap-4 cursor-pointer transition-all duration-300 hover:bg-danger-500/20 hover:border-danger-500/50 hover:shadow-lg hover:shadow-danger-500/10 hover:-translate-y-1 hover:scale-[1.02]"
                >
                  <div className="w-8 h-8 rounded-full bg-surface-700 text-surface-300 flex items-center justify-center font-bold transition-all duration-300 group-hover/card:bg-surface-600 group-hover/card:scale-110">
                    {insights.growthRankings.length - 4 + index}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-white transition-colors duration-300 group-hover/card:text-danger-200">{state.state}</div>
                  </div>
                  <div className={`text-2xl font-bold transition-all duration-300 group-hover/card:scale-110 ${state.percentGrowth >= 0 ? 'text-success-400 group-hover/card:text-success-300' : 'text-danger-400 group-hover/card:text-danger-300'}`}>
                    {state.percentGrowth >= 0 ? '+' : ''}
                    <AnimatedPercentage 
                      target={state.percentGrowth} 
                      decimals={0}
                      animate={isActive} 
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Sparkline cards for top growers */}
        <div ref={sparklineContainerRef}>
          <h3 className="text-xl font-semibold text-white mb-6 text-center">
            Growth Trajectories
          </h3>
          
          <div className="grid md:grid-cols-3 gap-6">
            {insights.growthRankings.slice(0, 3).map((state, index) => (
              <div
                key={state.state}
                className="growth-card group p-6 bg-surface-800/50 backdrop-blur-sm rounded-2xl border border-surface-700 cursor-pointer transition-all duration-300 hover:bg-surface-800/70 hover:border-success-500/30 hover:shadow-xl hover:shadow-success-500/10 hover:-translate-y-2 hover:scale-[1.02]"
                style={{ perspective: '1000px' }}
              >
                <div className="flex items-center gap-3 mb-4">
                  {index === 0 && <span className="text-2xl transition-transform duration-300 group-hover:scale-125 group-hover:rotate-12">🏆</span>}
                  {index === 1 && <span className="text-2xl transition-transform duration-300 group-hover:scale-125 group-hover:rotate-12">🥈</span>}
                  {index === 2 && <span className="text-2xl transition-transform duration-300 group-hover:scale-125 group-hover:rotate-12">🥉</span>}
                  <h4 className="font-semibold text-white transition-colors duration-300 group-hover:text-success-300">{state.state}</h4>
                </div>
                
                {/* Sparkline */}
                <div className="mb-4 transition-transform duration-300 group-hover:scale-105">
                  <svg viewBox="0 0 100 40" className="w-full h-12">
                    <defs>
                      <linearGradient id={`sparkGradient-${state.state}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#22c55e" stopOpacity="0.3" />
                        <stop offset="100%" stopColor="#22c55e" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    <path
                      d={generateSparkline(state.startTotal, state.endTotal)}
                      fill="none"
                      stroke="#22c55e"
                      strokeWidth="2"
                      strokeLinecap="round"
                      className="transition-all duration-300 group-hover:stroke-[3]"
                    />
                  </svg>
                </div>
                
                {/* Stats */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-surface-400 transition-colors duration-300 group-hover:text-surface-300">{insights.yearsSpan.start}</div>
                    <div className="font-semibold text-white">
                      ₦{(state.startTotal / 1e9).toFixed(1)}B
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-surface-400 transition-colors duration-300 group-hover:text-surface-300">{insights.yearsSpan.end}</div>
                    <div className="font-semibold text-success-400 transition-colors duration-300 group-hover:text-success-300">
                      ₦{(state.endTotal / 1e9).toFixed(1)}B
                    </div>
                  </div>
                </div>
                
                <div className="mt-4 pt-4 border-t border-surface-700 text-center transition-colors duration-300 group-hover:border-surface-600">
                  <span className="text-3xl font-bold text-success-400 transition-all duration-300 group-hover:text-success-300 group-hover:scale-110 inline-block">
                    +{Math.min(state.percentGrowth, 9999).toFixed(0)}%
                  </span>
                  <span className="text-surface-400 text-sm ml-2 transition-colors duration-300 group-hover:text-surface-300">growth</span>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Note */}
        <p className="mt-8 text-center text-surface-400 text-sm">
          Growth percentages compare total yearly allocations from {insights.yearsSpan.start} to {insights.yearsSpan.end}.
          Higher growth doesn't necessarily mean higher total allocations.
        </p>
      </div>
    </section>
  );
}

export default Scene6Growth;

